import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import { State as RootState } from '@/store'

export type UploadFileStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface UploadFile {
    id: string;
    name: string;
    size: number;
    status: UploadFileStatus;
    errorMessage?: string;
    uploadedSize: number;
}

export interface UploadSession {
    id: string;
    galleryId: string;
    galleryName: string;
    totalFiles: number;
    uploadedCount: number;
    totalSize: number;
    uploadedSize: number;
    files: UploadFile[];
    isComplete: boolean;
    isDismissed: boolean;
}

// Registry of retry handlers — stored outside Vuex (functions not serializable)
type RetryHandler = (fileId: string) => void;
const retryHandlers = new Map<string, RetryHandler>();

export function registerRetryHandler(sessionId: string, handler: RetryHandler) {
    retryHandlers.set(sessionId, handler);
}

export function unregisterRetryHandler(sessionId: string) {
    retryHandlers.delete(sessionId);
}

export function callRetryHandler(sessionId: string, fileId: string) {
    retryHandlers.get(sessionId)?.(fileId);
}

export type State = {
    sessions: UploadSession[];
}

export type Mutations<S = State> = {
    addSession(state: S, session: UploadSession): void;
    updateSessionProgress(state: S, payload: { id: string; uploadedCount: number; uploadedSize: number }): void;
    setFileStatus(state: S, payload: { sessionId: string; fileId: string; status: UploadFileStatus; errorMessage?: string }): void;
    updateFileProgress(state: S, payload: { sessionId: string; fileId: string; uploadedSize: number }): void;
    completeSession(state: S, payload: { id: string }): void;
    dismissSession(state: S, payload: { id: string }): void;
    removeSession(state: S, payload: { id: string }): void;
    removeDoneFiles(state: S, payload: { id: string }): void;
}

type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>

export interface Actions {
    startUploadSession(
        { commit }: AugmentedActionContext,
        payload: { id: string; galleryId: string; galleryName: string; files: UploadFile[]; totalSize: number }
    ): void;
    finishUploadSession(
        { commit, state }: AugmentedActionContext,
        payload: { id: string }
    ): void;
}

export type Getters = {
    activeSessions(state: State): UploadSession[];
    isUploading(state: State): boolean;
}

const state: State = {
    sessions: [],
}

const mutations: MutationTree<State> & Mutations = {
    addSession(state, session) {
        state.sessions.push(session);
    },
    updateSessionProgress(state, { id, uploadedCount, uploadedSize }) {
        const session = state.sessions.find(s => s.id === id);
        if (session) {
            session.uploadedCount = uploadedCount;
            session.uploadedSize = uploadedSize;
        }
    },
    setFileStatus(state, { sessionId, fileId, status, errorMessage }) {
        const session = state.sessions.find(s => s.id === sessionId);
        if (session) {
            const file = session.files.find(f => f.id === fileId);
            if (file) {
                file.status = status;
                if (errorMessage !== undefined) {
                    file.errorMessage = errorMessage;
                }
                if (status === 'uploading') {
                    file.uploadedSize = 0;
                }
            }
        }
    },
    updateFileProgress(state, { sessionId, fileId, uploadedSize }) {
        const session = state.sessions.find(s => s.id === sessionId);
        if (session) {
            const file = session.files.find(f => f.id === fileId);
            if (file) {
                file.uploadedSize = uploadedSize;
            }
        }
    },
    completeSession(state, { id }) {
        const session = state.sessions.find(s => s.id === id);
        if (session) {
            session.isComplete = true;
        }
    },
    dismissSession(state, { id }) {
        const session = state.sessions.find(s => s.id === id);
        if (session) {
            session.isDismissed = true;
        }
    },
    removeSession(state, { id }) {
        state.sessions = state.sessions.filter(s => s.id !== id);
    },
    removeDoneFiles(state, { id }) {
        const session = state.sessions.find(s => s.id === id);
        if (session) {
            session.files = session.files.filter(f => f.status !== 'done');
            session.totalFiles = session.files.length;
            session.uploadedCount = 0;
            session.uploadedSize = 0;
            session.isComplete = false;
        }
    },
}

const actions: ActionTree<State, RootState> & Actions = {
    startUploadSession({ commit }, payload) {
        const session: UploadSession = {
            id: payload.id,
            galleryId: payload.galleryId,
            galleryName: payload.galleryName,
            totalFiles: payload.files.length,
            uploadedCount: 0,
            totalSize: payload.totalSize,
            uploadedSize: 0,
            files: payload.files.map(f => ({ ...f, uploadedSize: 0 })),
            isComplete: false,
            isDismissed: false,
        };
        commit('addSession', session);
    },
    finishUploadSession({ commit }, { id }) {
        commit('completeSession', { id });
        // Сесія лишається видимою — компонент вирішує коли згортати/закривати
    },
}

const getters: GetterTree<State, RootState> & Getters = {
    activeSessions(state) {
        return state.sessions.filter(s => !s.isDismissed);
    },
    isUploading(state) {
        return state.sessions.some(s => !s.isComplete && !s.isDismissed);
    },
}

export type Store<S = State> = Omit<VuexStore<S>, 'getters' | 'commit' | 'dispatch'> & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions
    ): ReturnType<Mutations[K]>;
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload?: Parameters<Actions[K]>[1],
        options?: DispatchOptions
    ): ReturnType<Actions[K]>;
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
}

export const UploadModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
